import { collection, getDocs, query, orderBy } from 'firebase/firestore';
import { db } from './firebase';
import { openIndexedDBWithStores } from './indexedDBUtils';
import { conditionalLog } from './consoleLogger';

/**
 * Local-first Equipment Type Service for STB Workshop
 * Prioritizes IndexedDB cache with Firestore fallback to minimize reads
 */

// ===== EQUIPMENT TYPE DATA OPERATIONS =====

/**
 * Gets all equipment types with local-first architecture
 * Uses IndexedDB cache first, falls back to Firestore only when needed
 * @param {boolean} forceRefresh - Force refresh from Firestore
 * @returns {Promise<Array>} Array of equipment type objects with usage counts
 */
export const getAllTypes = async (forceRefresh = false) => {
  try {
    if (!forceRefresh) {
      // Try to get from IndexedDB cache first
      const cachedTypes = await getTypesFromCache();
      
      if (cachedTypes && cachedTypes.length > 0) {
        conditionalLog(`Using cached equipment type data (${cachedTypes.length} items)`);
        
        // Deserialize dates from cached data
        const deserializedTypes = cachedTypes.map(type => deserializeDates(type));
        
        // Sort manually by name
        deserializedTypes.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
        
        return deserializedTypes;
      }
    }
    
    // Cache miss or force refresh - fetch from Firestore and update cache
    conditionalLog('No cached equipment type data found or force refresh, fetching from Firestore...');
    return await fetchAndCacheTypes();
    
  } catch (error) {
    console.error('Error getting equipment types:', error);
    
    // Fallback to Firestore if IndexedDB fails
    try {
      return await fetchAndCacheTypes();
    } catch (firestoreError) {
      console.error('Error fetching equipment types from Firestore:', firestoreError);
      return [];
    }
  }
};

/**
 * Fetches equipment types from Firestore and updates IndexedDB cache
 * @returns {Promise<Array>} Array of equipment type objects with usage counts
 */
const fetchAndCacheTypes = async () => {
  try {
    const q = query(collection(db, 'tipos_equipo'), orderBy('nombre'));
    const querySnapshot = await getDocs(q);
    let typesList = querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Calculate usage counts
    typesList = await calculateTypeUsageCounts(typesList);
    
    // Update IndexedDB cache with fresh data
    await syncTypesToCache(typesList);
    
    conditionalLog(`Fetched ${typesList.length} equipment types from Firestore and updated cache`);
    return typesList;
    
  } catch (error) {
    console.error('Error fetching equipment types from Firestore:', error);
    throw error;
  }
};

/**
 * Calculate usage counts for equipment types by checking brands and equipment catalog
 * @param {Array} typesList - Array of equipment type objects
 * @returns {Promise<Array>} Types with usage counts
 */
const calculateTypeUsageCounts = async (typesList) => {
  try {
    // Get brands and equipment from cache or Firestore to count type usage
    const { getAllBrands } = await import('./brandService');
    const { getAllEquipment } = await import('./equipmentService');
    
    const [brandsList, equipmentList] = await Promise.all([
      getAllBrands(),
      getAllEquipment(true) // Include archived for accurate counts
    ]);
    
    return typesList.map(type => {
      let brandUsageCount = 0;
      let equipmentCount = 0;
      
      // Count usage in brands
      brandsList.forEach(brand => {
        // Check new format (tiposEquipoIds array)
        if (brand.tiposEquipoIds && Array.isArray(brand.tiposEquipoIds)) {
          if (brand.tiposEquipoIds.includes(type.id)) {
            brandUsageCount++;
          }
        }
        
        // Check old format (tiposEquipo array) for backward compatibility
        if (brand.tiposEquipo && Array.isArray(brand.tiposEquipo)) {
          if (brand.tiposEquipo.includes(type.nombre)) {
            brandUsageCount++;
          }
        }
      });
      
      // Count usage in equipment catalog
      equipmentList.forEach(equipment => {
        // Check by type ID (new format)
        if (equipment.tipoId === type.id) {
          equipmentCount++;
        }
        
        // Check by type name (old format for backward compatibility)
        if (equipment.tipo === type.nombre) {
          equipmentCount++;
        }
      });
      
      return {
        ...type,
        brandUsageCount,
        equipmentCount
      };
    });
  } catch (error) {
    console.error('Error calculating type usage counts:', error);
    // Return original list if calculation fails
    return typesList.map(type => ({ 
      ...type, 
      brandUsageCount: 0, 
      equipmentCount: 0 
    }));
  }
};

// ===== INDEXEDDB CACHE OPERATIONS =====

/**
 * Get equipment types from IndexedDB cache
 * @returns {Promise<Array|null>} Cached equipment types or null if not found
 */
const getTypesFromCache = async () => {
  try {
    const db = await openIndexedDBWithStores();
    
    return new Promise((resolve) => {
    
      // Database already opened, proceed with transaction
      
      if (!db.objectStoreNames.contains('equipment_types')) {
        conditionalLog('Equipment types store does not exist in IndexedDB');
        db.close();
        resolve(null);
        return;
      }
      
      const transaction = db.transaction(['equipment_types'], 'readonly');
      const store = transaction.objectStore('equipment_types');
      const getAllRequest = store.getAll();
      
      getAllRequest.onsuccess = () => {
        db.close();
        const types = getAllRequest.result || [];
        resolve(types);
      };
      
      getAllRequest.onerror = () => {
        console.error('Error reading equipment types from IndexedDB cache');
        db.close();
        resolve(null);
      };
    });
  } catch (error) {
    console.error('Error getting types from cache:', error);
    return null;
  }
};

/**
 * Sync equipment types to IndexedDB cache
 * @param {Array} types - Array of equipment type objects to cache
 * @returns {Promise<boolean>} Success status
 */
const syncTypesToCache = async (types) => {
  try {
    const db = await openIndexedDBWithStores();
    
    return new Promise((resolve) => {
    
      // Database and stores already created, proceed with transaction
      
      // Ensure the equipment_types store exists
      if (!db.objectStoreNames.contains('equipment_types')) {
        conditionalLog('Equipment types store does not exist, will be created on next upgrade');
        db.close();
        resolve(false);
        return;
      }
      
      const transaction = db.transaction(['equipment_types'], 'readwrite');
      const store = transaction.objectStore('equipment_types');
      
      // Clear existing data
      const clearRequest = store.clear();
      
      clearRequest.onsuccess = () => {
        // Add all types with serialized dates
        const serializedTypes = types.map(type => serializeDates(type));
        
        let completed = 0;
        const total = serializedTypes.length;
        
        if (total === 0) {
          db.close();
          resolve(true);
          return;
        }
        
        serializedTypes.forEach(type => {
          const addRequest = store.add(type);
          
          addRequest.onsuccess = () => {
            completed++;
            if (completed === total) {
              db.close();
              conditionalLog(`Synced ${total} equipment types to IndexedDB cache`);
              resolve(true);
            }
          };
          
          addRequest.onerror = () => {
            console.error('Error adding equipment type to IndexedDB cache:', type.id);
            completed++;
            if (completed === total) {
              db.close();
              resolve(false);
            }
          };
        });
      };
      
      clearRequest.onerror = () => {
        console.error('Error clearing equipment types from IndexedDB cache');
        db.close();
        resolve(false);
      };
    });
  } catch (error) {
    console.error('Error syncing types to cache:', error);
    return false;
  }
};

// ===== DATE SERIALIZATION UTILITIES =====

/**
 * Serialize Firestore Timestamps to ISO strings for IndexedDB storage
 * @param {Object} type - Equipment type object with potential Firestore Timestamps
 * @returns {Object} Equipment type object with serialized dates
 */
const serializeDates = (type) => {
  const serialized = { ...type };
  
  // Convert Firestore Timestamps to ISO strings
  if (serialized.createdAt?.toDate) {
    serialized.createdAt = serialized.createdAt.toDate().toISOString();
  }
  if (serialized.updatedAt?.toDate) {
    serialized.updatedAt = serialized.updatedAt.toDate().toISOString();
  }
  
  return serialized;
};

/**
 * Deserialize ISO strings back to Date objects after reading from IndexedDB
 * @param {Object} type - Equipment type object with serialized dates
 * @returns {Object} Equipment type object with Date objects
 */
const deserializeDates = (type) => {
  const deserialized = { ...type };
  
  // Convert ISO strings back to Date objects
  if (typeof deserialized.createdAt === 'string') {
    deserialized.createdAt = new Date(deserialized.createdAt);
  }
  if (typeof deserialized.updatedAt === 'string') {
    deserialized.updatedAt = new Date(deserialized.updatedAt);
  }
  
  return deserialized;
};

// ===== SEARCH OPERATIONS =====

/**
 * Search equipment types locally from IndexedDB cache
 * @param {string} searchTerm - Search term
 * @param {Array} specs - Array of specifications for cross-referencing
 * @returns {Promise<Array>} Filtered equipment type results
 */
export const searchTypesLocal = async (searchTerm, specs = []) => {
  try {
    const types = await getAllTypes();
    
    if (!searchTerm) return types;
    
    const searchTermLower = searchTerm.toLowerCase();
    
    const getSpecNameById = (specId) => {
      const spec = specs.find(s => s.id === specId);
      return spec ? spec.nombre : 'Especificación desconocida';
    };
    
    return types.filter(type => {
      // Search by name
      if (type.nombre?.toLowerCase().includes(searchTermLower)) {
        return true;
      }
      
      // Search by specifications (new format)
      if (type.specsIds && Array.isArray(type.specsIds)) {
        return type.specsIds.some(specId => {
          const specName = getSpecNameById(specId);
          return specName.toLowerCase().includes(searchTermLower);
        });
      }
      
      // Search by specifications (old format for backward compatibility)
      if (type.especificaciones && Array.isArray(type.especificaciones)) {
        return type.especificaciones.some(spec => 
          spec.toLowerCase().includes(searchTermLower)
        );
      }
      
      return false;
    });
  } catch (error) {
    console.error('Error searching equipment types locally:', error);
    return [];
  }
};

/**
 * Check if an equipment type is used in brands or equipment catalog
 * @param {string} typeName - Equipment type name to check
 * @returns {Promise<boolean>} Whether type is in use
 */
export const checkTypeInUse = async (typeName) => {
  try {
    const { getAllBrands } = await import('./brandService');
    const { getAllEquipment } = await import('./equipmentService');
    
    const [brandsList, equipmentList] = await Promise.all([
      getAllBrands(),
      getAllEquipment(true) // Include archived
    ]);
    
    // Check if type is used in brands
    const isUsedInBrands = brandsList.some(brand => {
      return brand.tiposEquipo?.includes(typeName);
    });

    if (isUsedInBrands) return true;

    // Check if type is used in equipment catalog
    const isUsedInEquipment = equipmentList.some(equipment => {
      return equipment.tipo === typeName;
    });

    return isUsedInEquipment;
  } catch (error) {
    console.error('Error checking equipment type usage:', error);
    return true; // Assume type is in use if there's an error
  }
};